import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import { ORGANIZATION_QUERY_KEY, ORGANIZATIONS_QUERY_KEY, OrganizationWritePayload } from './types';

const invalidateOrg = (queryClient: ReturnType<typeof useQueryClient>, id?: string) => {
  queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
  if (id) queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEY(id) });
};

export const useCreateOrganization = (
  options?: UseMutationOptions<{ id: string }, AxiosError, OrganizationWritePayload>,
): UseMutationResult<{ id: string }, AxiosError, OrganizationWritePayload> => {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, AxiosError, OrganizationWritePayload>({
    mutationFn: async (data) => {
      const res = await axiosInstance.post('/v3-createOrganization', data);
      return res.data.data as { id: string };
    },
    ...options,
    onSuccess: (...args) => {
      invalidateOrg(queryClient);
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateOrganization = (
  options?: UseMutationOptions<void, AxiosError, OrganizationWritePayload & { id: string }>,
): UseMutationResult<void, AxiosError, OrganizationWritePayload & { id: string }> => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, OrganizationWritePayload & { id: string }>({
    mutationFn: async (data) => {
      await axiosInstance.put('/v3-updateOrganization', data);
    },
    ...options,
    onSuccess: (...args) => {
      invalidateOrg(queryClient, args[1].id);
      options?.onSuccess?.(...args);
    },
  });
};

export const useSetOrganizationStatus = (
  options?: UseMutationOptions<void, AxiosError, { id: string; isDisabled: boolean }>,
): UseMutationResult<void, AxiosError, { id: string; isDisabled: boolean }> => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, { id: string; isDisabled: boolean }>({
    mutationFn: async ({ id, isDisabled }) => {
      await axiosInstance.put('/v3-setOrganizationStatus', { id, isDisabled });
    },
    ...options,
    onSuccess: (...args) => {
      invalidateOrg(queryClient, args[1].id);
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateOrgAccountManager = (
  options?: UseMutationOptions<void, AxiosError, { id: string; account_manager?: string }>,
): UseMutationResult<void, AxiosError, { id: string; account_manager?: string }> => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, { id: string; account_manager?: string }>({
    mutationFn: async ({ id, account_manager }) => {
      await axiosInstance.put('/v3-updateOrgAccountManager', { id, account_manager });
    },
    ...options,
    onSuccess: (...args) => {
      invalidateOrg(queryClient, args[1].id);
      options?.onSuccess?.(...args);
    },
  });
};

export const useAssignOrganizationAttractions = (
  options?: UseMutationOptions<void, AxiosError, { id: string; attractionIds: string[] }>,
): UseMutationResult<void, AxiosError, { id: string; attractionIds: string[] }> => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, { id: string; attractionIds: string[] }>({
    mutationFn: async ({ id, attractionIds }) => {
      await axiosInstance.put('/v3-assignOrganizationAttractions', { id, attractionIds });
    },
    ...options,
    onSuccess: (...args) => {
      invalidateOrg(queryClient, args[1].id);
      options?.onSuccess?.(...args);
    },
  });
};

export const checkDomainAvailability = async (
  domain: string,
  exceptId?: string,
): Promise<boolean> => {
  const res = await axiosInstance.get('/v3-checkDomainAvailability', {
    params: { domain, exceptId: exceptId || undefined },
  });
  return res.data?.data?.available === true;
};
